import { DEFAULT_WINDOWING } from "../constants/windowPresets";
/**
 * useSlotState - Custom hook for accessing and controlling a hanging protocol slot
 *
 * Uses atomFamily pattern for per-slot isolation:
 * - Each slot has its own atom (no cross-slot interference)
 * - Direct atom access (no dispatch overhead)
 * - Scales to any number of slots
 */

import { useCallback, useMemo } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  slotStateFamily,
  activeSlotAtom,
  volumeStatusFamily,
  maximizedSlotAtom,
  toggleInvertAtom,
  resetSlotViewAtom,
} from "../atoms/hangingProtocolAtoms";

export function useSlotState(slotId: string) {
  // Direct access to this slot's atom (isolated from other slots)
  const [slotState, setSlotState] = useAtom(slotStateFamily(slotId));
  const [activeSlot, setActiveSlot] = useAtom(activeSlotAtom);
  const setVolumeStatus = useSetAtom(volumeStatusFamily(slotState.seriesId));
  const maximizedSlot = useAtomValue(maximizedSlotAtom);
  const toggleInvert = useSetAtom(toggleInvertAtom);
  const resetSlotView = useSetAtom(resetSlotViewAtom);

  const isActive = activeSlot === slotId;
  const isMaximized = maximizedSlot === slotId;

  // Direct state setters (no dispatch overhead)
  const setSliceIndex = useCallback(
    (sliceIndex: number) => {
      setSlotState((prev) => ({ ...prev, sliceIndex }));
    },
    [setSlotState],
  );

  const setWindowing = useCallback(
    (center: number, width: number) => {
      setSlotState((prev) => ({ ...prev, windowing: { center, width } }));
    },
    [setSlotState],
  );

  const setInitialWindowing = useCallback(
    (initialWindowing: { center: number; width: number }) => {
      setSlotState((prev) => ({ ...prev, initialWindowing }));
    },
    [setSlotState],
  );

  const setActive = useCallback(() => {
    setActiveSlot(slotId);
  }, [setActiveSlot, slotId]);

  const setTotalSlices = useCallback(
    (total: number) => {
      setSlotState((prev) => ({ ...prev, totalSlices: total }));
    },
    [setSlotState],
  );

  const setListAnatomicSign = useCallback(
    (sign: -1 | 0 | 1) => {
      setSlotState((prev) =>
        prev.listAnatomicSign === sign ? prev : { ...prev, listAnatomicSign: sign },
      );
    },
    [setSlotState],
  );

  const setZoom = useCallback(
    (zoom: number) => {
      setSlotState((prev) => ({ ...prev, zoom }));
    },
    [setSlotState],
  );

  const setPan = useCallback(
    (pan: [number, number]) => {
      setSlotState((prev) => ({ ...prev, pan }));
    },
    [setSlotState],
  );

  const assignSeries = useCallback(
    (
      seriesId: string,
      windowing?: { center: number; width: number },
      isInverted: boolean = false,
    ) => {
      setSlotState(() => {
        // Same race fix as the SET_SERIES action — fall back to
        // DEFAULT_WINDOWING rather than the prior slot's windowing so a
        // Reset firing before the ProtocolViewport seeding effect can't
        // trap the previous series's W/L. See hangingProtocolAtoms.ts
        // SET_SERIES for the same fix.
        const nextWindowing = windowing ?? { ...DEFAULT_WINDOWING };
        return {
          seriesId,
          sliceIndex: 0,
          totalSlices: 0,
          windowing: nextWindowing,
          initialWindowing: { ...nextWindowing },
          zoom: 1,
          pan: [0, 0],
          loadingState: { isLoading: false, loadedFrames: 0, totalFrames: 0 },
          isInverted,
          pendingJump: null,
          listAnatomicSign: 0,
        };
      });
    },
    [setSlotState],
  );

  const setLoadingState = useCallback(
    (isLoading: boolean, loadedFrames: number, totalFrames: number) => {
      setSlotState((prev) => ({
        ...prev,
        loadingState: { isLoading, loadedFrames, totalFrames },
      }));
    },
    [setSlotState],
  );

  // For updating volume status (keyed by seriesId, not slotId)
  const updateVolumeStatus = useCallback(
    (status: "pending" | "loading" | "ready" | "error") => {
      if (slotState.seriesId) {
        setVolumeStatus(status);
      }
    },
    [slotState.seriesId, setVolumeStatus],
  );

  // Toggle invert for this slot
  const toggleInvertView = useCallback(() => {
    toggleInvert(slotId);
  }, [toggleInvert, slotId]);

  // Reset view for this slot
  const resetView = useCallback(() => {
    resetSlotView(slotId);
  }, [resetSlotView, slotId]);

  // Promote the load-time cross-series jump target into the authoritative
  // sliceIndex once the volume is ready, then clear it. The single promote
  // site (called from ProtocolViewport's volume-ready gate). Functional update
  // so concurrent calls converge; no-op once cleared.
  const promotePendingJump = useCallback(() => {
    setSlotState((prev) => {
      if (prev.pendingJump === null) {
        return prev; // No-op - prevents setting sliceIndex to null
      }
      return {
        ...prev,
        sliceIndex: prev.pendingJump,
        pendingJump: null,
      };
    });
  }, [setSlotState]);

  return useMemo(
    () => ({
      // State
      seriesId: slotState.seriesId,
      sliceIndex: slotState.sliceIndex,
      totalSlices: slotState.totalSlices,
      windowing: slotState.windowing,
      zoom: slotState.zoom,
      pan: slotState.pan,
      loadingState: slotState.loadingState,
      isActive,
      isMaximized,
      isInverted: slotState.isInverted,
      pendingJump: slotState.pendingJump,
      listAnatomicSign: slotState.listAnatomicSign,

      // Actions
      setSliceIndex,
      setWindowing,
      setInitialWindowing,
      setActive,
      setTotalSlices,
      setListAnatomicSign,
      setZoom,
      setPan,
      assignSeries,
      setLoadingState,
      updateVolumeStatus,
      toggleInvertView,
      resetView,
      promotePendingJump,
    }),
    [
      slotState,
      isActive,
      isMaximized,
      setSliceIndex,
      setWindowing,
      setInitialWindowing,
      setActive,
      setTotalSlices,
      setListAnatomicSign,
      setZoom,
      setPan,
      assignSeries,
      setLoadingState,
      updateVolumeStatus,
      toggleInvertView,
      resetView,
      promotePendingJump,
    ],
  );
}
